import { denormalizeReport, normalizeReport } from '@/components/report-tables/model'
import { headerTypes } from '@/components/report-tables/enums/headerTypes'
import keys from 'lodash/keys'
import { definitionType } from '@/enums/definitionType'
import { reportHeaderType } from '@/enums/reportHeaderType'

function processDesignerReport (report) {
  const unifiedReport = unifyTrees(report)
  processTree(unifiedReport.cols.children, unifiedReport.cols)
  processTree(unifiedReport.rows.children, unifiedReport.rows)

  return processReport(unifiedReport, false)
}

function addValueToKey (collection, key, value) {
  if (key === undefined || key === null) key = 'root'

  if (!collection.hasOwnProperty(key)) collection[key] = []

  collection[key].push(value)
}

function processUserReport (report) {
  const unifiedReport = unifyTrees(report)

  const processedReport = processReport(unifiedReport, true)

  if (processedReport.allRows) {
    processedReport.bankDefResidentCodes = {}
    processedReport.bankDefNonResidentCodes = {}
    processedReport.bankDefCodes = {}
    processedReport.branchDefCodes = {}
    processedReport.userDefCodes = {}

    Object.keys(processedReport.allRows).forEach(key => {
      const header = processedReport.allRows[key]
      if (header.fromDefinitionType) {
        switch (header.fromDefinitionType) {
          case definitionType.enum.BankDefResident:
            addValueToKey(processedReport.bankDefResidentCodes, header.parentId, header.code)
            break
          case definitionType.enum.BankDefNonResident:
            addValueToKey(processedReport.bankDefNonResidentCodes, header.parentId, header.code)
            break
          case definitionType.enum.BankDef:
            addValueToKey(processedReport.bankDefCodes, header.parentId, header.code)
            break
          case definitionType.enum.BranchDef:
            addValueToKey(processedReport.branchDefCodes, header.parentId, header.code)
            break
          case definitionType.enum.UserDef:
            addValueToKey(processedReport.userDefCodes, header.parentId, header.code)
            break
        }
      }
    })
  }

  return processedReport
}

function processReport (unifiedReport, editableUserRows) {
  processUserRowChildren(unifiedReport.rows, editableUserRows, unifiedReport.metadata ? unifiedReport.metadata.userDefRowsNo : null)
  processHasData(unifiedReport.cols)
  unifiedReport.isEditableOutputReport = unifiedReport.metadata ? unifiedReport.metadata.isEditableOutputReport : false
  return normalizeReport(unifiedReport)
}

function unprocessReport (report) {
  const r = { ...report }
  delete r.version
  keys(r.allCols).forEach(key => {
    r.allCols[key] = unprocessCell(r.allCols[key], r.allCols)
  })
  keys(r.allRows).forEach(key => {
    r.allRows[key] = unprocessCell(r.allRows[key], r.allRows)
    if (r.allRows[key].userDefRowsNo && !r.metadata) {
      var userDefRowObj = {}
      userDefRowObj[key] = r.allRows[key].userDefRowsNo
      r.metadata = {
        'userDefRowsNo': userDefRowObj
      }
      delete r.allRows[key].userDefRowsNo
    } else if (r.allRows[key].userDefRowsNo) {
      r.metadata.userDefRowsNo[key] = r.allRows[key].userDefRowsNo
      delete r.allRows[key].userDefRowsNo
    }
  })
  if (r.isEditableOutputReport) {
    r.metadata = r.metadata || {}
    r.metadata.isEditableOutputReport = r.isEditableOutputReport

    delete r.isEditableOutputReport
  }
  return denormalizeReport(r)
}

function unprocessCell (cell, allCells) {
  cell = { ...cell }
  if (cell.children && cell.children.length === 0) {
    delete cell.children
  } else if (cell.children.length > 0) {
    cell.children.sort((a, b) => allCells[a].order - allCells[b].order)
  }
  delete cell.parent
  delete cell.parentId
  delete cell.sameDepthAsParent
  delete cell.id
  delete cell.treeCode
  return cell
}

function unifyTrees (report) {
  const cols = {
    id: -1,
    name: 'root',
    latinName: 'root',
    treeCode: '',
    order: 1,
    children: report.cols
  }

  for (let i = 0; i < report.cols.length; i++) {
    report.cols[i].parentId = cols.id
  }

  const rows = {
    id: -1,
    name: report.rowTitle,
    latinName: report.latinRowTitle,
    treeCode: '',
    order: 1,
    type: headerTypes.Root,
    reportHeaderType: headerTypes.Root,
    children: report.rows
  }

  for (let i = 0; i < report.rows.length; i++) {
    report.rows[i].parentId = rows.id
  }

  return {
    ...report,
    cells: report.cells || report.reportTemplateCells || [],
    cols,
    rows
  }
}

function processTree (items, parent) {
  if (items && items.length > 0) {
    items.forEach((item, i) => {
      item.parentId = parent.id
      item.treeCode = parent.treeCode ? `${parent.treeCode}.${item.code}` : item.code
      if (!item.id) item.id = item.treeCode
      item.order = i + 1
      if (item.children) {
        processTree(item.children, item)
      } else {
        item.children = []
      }
    })

    // prevent browser crash
    const duplicities = items
      .map(x => ({ treeCode: x.treeCode, count: items.filter(y => y.treeCode === x.treeCode).length }))
      .filter(x => x.count > 1)
      .map(x => x.treeCode).filter((x, i, a) => a.indexOf(x) === i) // distinct
    if (duplicities.length > 0) {
      throw new Error('There are header code duplicities: ' + duplicities.join(', '))
    }
  }
}

function processHasData (nodes) {
  const processInternal = (node) => {
    for (let i = 0; i < node.children.length; i++) {
      const currentChild = node.children[i]

      currentChild.sameDepthAsParent = currentChild.treeCode === node.treeCode && node.hasData

      processInternal(currentChild)
    }
  }

  processInternal(nodes)
}

function processUserRowChildren (nodes, editableUserRows, userDefRowsNo = null) {
  const processInternal = (node) => {
    if (editableUserRows && (node.definitionType === definitionType.enum.BankDef ||
      node.definitionType === definitionType.enum.BranchDef ||
      node.definitionType === definitionType.enum.UserDef ||
      node.definitionType === definitionType.enum.BankDefResident ||
      node.definitionType === definitionType.enum.BankDefNonResident)) {
      node.children = []
      node.reportHeaderType = reportHeaderType.enum.LineHeader
    }

    for (let i = 0; i < node.children.length; i++) {
      const currentChild = node.children[i]
      if (userDefRowsNo && userDefRowsNo[currentChild.id]) {
        currentChild.userDefRowsNo = userDefRowsNo[currentChild.id]
      }
      processInternal(currentChild)
    }
  }

  processInternal(nodes)
}

function assignMetadata (changes, metadata) {
  for (let i = 0; i < changes.length; i++) {
    const change = changes[i]

    for (let j = 0; j < metadata.length; j++) {
      const meta = metadata[j]

      if (change.rowTreeCode === meta.rowTreeCode && change.columnTreeCode === meta.colTreeCode) {
        change.comment = meta.comment
        change.warnings = change.warnings || meta.warnings
        change.errors = change.errors || meta.errors
      }
    }
  }
}

function mergeSourceData (existingSourceData, dataToMerge) {
  const result = [...existingSourceData]
  const indexed = {}
  for (let i = 0; i < result.length; i++) {
    const item = result[i]

    indexed[`${item.headerRowId}${item.headerColId}`] = item
  }

  for (let i = 0; i < dataToMerge.length; i++) {
    const item = dataToMerge[i]
    const target = indexed[`${item.headerRowId}${item.headerColId}`]

    if (target) target.data = item.data
  }

  return result
}

function mergeMetadata (existingMetadata, metadataToMerge) {
  const result = [...existingMetadata]
  const index = {}

  for (let i = 0; i < existingMetadata.length; i++) {
    const item = result[i]
    index[`${item.rowTreeCode}x${item.colTreeCode}`] = item
  }

  for (let i = 0; i < metadataToMerge.length; i++) {
    const item = metadataToMerge[i]
    const target = index[`${item.rowTreeCode}x${item.colTreeCode}`]

    if (target) target.data = item.data
  }
  return result
}

function getCellMetadataFromReportSubmissionData (reportSubmissionData, existingMetadata = []) {
  const cellMetadata = []

  for (let i = 0; i < reportSubmissionData.length; i++) {
    const data = reportSubmissionData[i]
    const existing = existingMetadata.find(x => x.row === data.rowId && x.col === data.colId)
    const existingBsrsMetadata = existing ? existing.bsrsMetadata ? existing.bsrsMetadata : {} : {}

    let currentMetadata = null
    let bsrsMetadata = null

    if (data.hasFormula || (existing && existing.hasFormula)) currentMetadata = { ...currentMetadata, readOnly: true }
    if (data.isDisabled || (existing && existing.isDisabled)) currentMetadata = { ...currentMetadata, readOnly: true, className: 'ht-disabled' }
    if (data.colspan || (existing && existing.colspan)) currentMetadata = { ...currentMetadata, colspan: data.colspan }

    if (data.comment || existingBsrsMetadata.comment) bsrsMetadata = { ...bsrsMetadata, comment: data.comment }
    if (data.warnings || existingBsrsMetadata.warnings) bsrsMetadata = { ...bsrsMetadata, warnings: data.warnings }
    if (data.errors || existingBsrsMetadata.errors) {
      bsrsMetadata = { ...bsrsMetadata, errors: data.errors }
    }

    if (currentMetadata || bsrsMetadata) {
      cellMetadata.push({
        colTreeCode: data.columnTreeCode,
        rowTreeCode: data.rowTreeCode,
        ...currentMetadata,
        ...bsrsMetadata
      })
    }
  }

  for (let i = 0; i < existingMetadata.length; i++) {
    const metadata = existingMetadata[i]

    if (!cellMetadata.filter(x => x.row === metadata.row && x.col === metadata.col).length) cellMetadata.push(metadata)
  }

  return cellMetadata
}

function getExistingUserRowCodes (object, parentId) {
  if (parentId === undefined || parentId === null) parentId = 'root'

  return object.hasOwnProperty(parentId) ? object[parentId] : []
}

export {
  processDesignerReport,
  processReport,
  processUserReport,
  unprocessReport,
  assignMetadata,
  getCellMetadataFromReportSubmissionData,
  processUserRowChildren,
  getExistingUserRowCodes,
  mergeSourceData,
  mergeMetadata
}



// WEBPACK FOOTER //
// ./src/store/utils/reportUtils.js